import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { getReferralCode } from 'store/account';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import { copyToClipboard } from 'helpers';

import { Card, Icon, Typography } from 'components';

import styles from './ReferralWidget.module.scss';

export const ReferralWidget = () => {
  const { trackEvent } = useAnalytics();
  const referralCode = useSelector(getReferralCode);

  const onShareClick = async () => {
    if (!referralCode) {
      toast.error('Failed to share');
      return;
    }
    const messageToShare = `Check out Tiny Health, the most complete gut test for babies and toddlers. Use my discount code ${referralCode} at checkout for $40 off your first order: https://www.tinyhealth.com/store/`;
    try {
      if (navigator?.share) {
        await navigator.share({
          text: messageToShare,
        });
      } else {
        await navigator.clipboard.writeText(messageToShare);
        toast.success('Message copied to clipboard');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to share');
    }
  };

  const copyReferralCode = useCallback(() => {
    if (referralCode) {
      copyToClipboard(referralCode);
      trackEvent('Clicked Button', {
        text: 'Copy code',
        href: '',
        variety: 'Button',
      });
      toast.success('Referral code copied to clipboard');
    }
  }, [referralCode, trackEvent]);

  const sendEmail = useCallback(() => {
    trackEvent('Clicked Button', {
      text: 'Send Email',
      href: '',
      variety: 'Button',
    });
    const subject = encodeURIComponent(`$40 off gut test with my code`);
    const body = encodeURIComponent(
      `Check out Tiny Health, the most complete gut test for all ages. Use my discount code ${referralCode} at checkout for $40 off your first order: https://www.tinyhealth.com/store/`,
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  }, [referralCode, trackEvent]);

  if (!referralCode) return <></>;
  return (
    <Card variant='wrapper' className={styles.referralWidgetContainer}>
      <div className={styles.referralWidgetContent}>
        <div className={styles.referralWidgetHeader}>
          <div className={styles.referralWidgetIcon}>
            <Icon name='gift' />
          </div>
          <div className='flex flex-column gap-2'>
            <Typography variant='heading-l'>
              {`Invite a friend and you'll both get $40`}
            </Typography>
            <Typography variant='label'>
              {`Invite your friends to give them $40 off their kit and get $40 in
              store credit you can apply to anything you'd like when they use
              this code:`}
            </Typography>
          </div>
        </div>
        <div className='w-100'>
          <div className={styles.referralWidgetActions}>
            <div className={styles.referralWidgetButton} onClick={onShareClick}>
              <Typography variant='heading-s'>Share</Typography>
              <Icon name='share' size='s' />
            </div>
            <div className={styles.referralWidgetButton} onClick={sendEmail}>
              <Typography variant='heading-s'>Send Email</Typography>
              <Icon name='mail' size='s' />
            </div>
            <div
              className={styles.referralWidgetButton}
              onClick={copyReferralCode}
            >
              <Typography variant='heading-s'>{referralCode}</Typography>
              <Icon name='copy' size='s' />
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};
